/**
 * The single axios instance (Option A), built from the vendored fleet factory with tmm-ui's
 * config. Two surfaces, strictly separated:
 *
 *  - saas-auth COOKIE surface (`/api/auth/**`): base = `getBootstrap().authOrigin` (verbatim,
 *    never derived); `withCredentials: true`; CSRF double-submit (cookie-echo: `X-CSRF-Token`
 *    header + `csrfToken` body, cookie-first with a `GET /api/auth/csrf` fallback) on mutating
 *    calls. NO bearer.
 *  - tmm-api BEARER surface (everything else — `/v1/**`): base = `env.apiBaseUrl`
 *    (this origin + `/api`, rewritten at the edge); `Authorization: Bearer <token>`;
 *    `withCredentials: false`; NO CSRF — tmm-api is a stateless bearer-only resource server
 *    (MM-D-00). The platform CSP allows exactly these two origins.
 *
 * A `401` on the bearer surface triggers ONE silent `POST /api/auth/refresh` and ONE retry —
 * bounded by the factory's `_retry` guard. A `401` anywhere on the auth surface (the refresh
 * itself included) is terminal: those requests are pre-marked `_retry` below so the factory
 * never refreshes them.
 */
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { createApiClient } from './httpClientFactory';
import { ApiError, standingRefusal } from './errors';
import { retryAfterTransform } from './retryAfter';
import { env } from '@/config/env';
import { getBootstrap } from '@/config/bootstrap';
import {
  getAccessToken,
  getCsrfToken,
  setAccessToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import { generateRequestId } from '@/utils/requestId';
import { filenameFromContentDisposition } from '@/utils/contentDisposition';

/** What a binary GET (`export.xlsx`) accepts in place of the factory's `application/json`. */
export const BLOB_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream';

/** Dispatched with the `StandingRefusal` as `detail`; `MeProvider` refetches `/me` and says why. */
export const STANDING_REFUSED_EVENT = 'tmm:standing-refused';

export function isAuthSurface(path: string): boolean {
  return path.startsWith('/api/auth/');
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const instance: AxiosInstance = createApiClient({
  resolveBaseUrl: (path) => (isAuthSurface(path) ? getBootstrap().authOrigin : env.apiBaseUrl),
  sendCredentials: isAuthSurface,
  tokens: {
    getAccess: getAccessToken,
    setAccess: (token, expiresAt) => setAccessToken(token, expiresAt ?? null),
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: (path) => !isAuthSurface(path) },
  csrf: {
    protected: (path, method) => isAuthSurface(path) && MUTATING.has(method),
    mode: () => 'cookie-echo',
  },
  refresh: { path: '/api/auth/refresh' },
  onSessionExpired: () => signOut({ expired: true }),
  generateRequestId,
});

// Scope the silent refresh to the bearer surface: pre-marking every auth-surface request as
// `_retry` makes the factory's `!_retry` gate skip it, so a 401 from refresh / logout / csrf is
// normalized, never refreshed (and the refresh POST can never recurse).
instance.interceptors.request.use((req) => {
  const r = req as { _retry?: boolean };
  if (isAuthSurface(pathOf(req)) && r._retry === undefined) r._retry = true;
  return req;
});

// The factory's `ApiError` carries no response headers, so a 429 / 503 `Retry-After` is copied into
// the body's `error.details` before the factory normalizes it (TMMAPP-0068); see `retryAfter.ts`.
const defaultTransforms = instance.defaults.transformResponse;
instance.defaults.transformResponse = [
  ...(Array.isArray(defaultTransforms)
    ? defaultTransforms
    : defaultTransforms === undefined
      ? []
      : [defaultTransforms]),
  retryAfterTransform,
];

instance.interceptors.response.use(undefined, (err: unknown) => {
  if (
    err instanceof ApiError &&
    (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'USER_FROZEN')
  ) {
    window.dispatchEvent(new CustomEvent('tmm:session-locked'));
  }
  // A refusal by the caller's standing on a virtual account (TMMAPI-0146): the session refetches the
  // served role flags and says why, once, wherever the write came from.
  const refusal = standingRefusal(err);
  if (refusal !== null) {
    window.dispatchEvent(new CustomEvent(STANDING_REFUSED_EVENT, { detail: refusal }));
  }
  throw err;
});

function pathOf(req: { url?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

export interface RequestOptions {
  json?: unknown;
  /** Multipart body sent verbatim; `Content-Type` is omitted so the browser sets the boundary. */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  /** `Idempotency-Key` for the endpoints that accept one (design §8.1). */
  idempotencyKey?: string;
  /** `'blob'` resolves the body as a `Blob` and asks for the binary types (`BLOB_ACCEPT`). */
  responseType?: 'blob';
  /** Overrides the blob `Accept` (ICS uses `text/calendar`). */
  accept?: string;
  /** Axios upload progress. Passed through by reference; callers report it, the client does not. */
  onUploadProgress?: AxiosRequestConfig['onUploadProgress'];
  /** Aborts the request (a picture that left the view, TMMAPP-0142 M8). */
  signal?: AbortSignal;
}

function buildConfig(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions,
): AxiosRequestConfig {
  const headers: Record<string, string> = { ...options.headers };
  if (options.json !== undefined && options.form === undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.responseType === 'blob') {
    headers.Accept = options.accept ?? BLOB_ACCEPT;
  }
  const config: AxiosRequestConfig = { method, url: path, headers };
  if (options.form !== undefined) {
    config.data = options.form;
  } else if (options.json !== undefined) {
    config.data = options.json;
  }
  if (options.query !== undefined) config.params = options.query;
  if (options.responseType === 'blob') {
    config.responseType = 'blob';
  }
  if (options.onUploadProgress !== undefined) {
    config.onUploadProgress = options.onUploadProgress;
  }
  if (options.signal !== undefined) config.signal = options.signal;
  return config;
}

/**
 * Issue one request and return its body. Throws the factory's `ApiError` (see `./errors`).
 * `path` is the bare path (`/v1/me`, `/api/auth/refresh`); the base is resolved per surface.
 */
export interface RequestMeta<T> {
  data: T;
  status: number;
  location: string | null;
  /** A response header's value by name (any case), or null when absent or empty. */
  header: (name: string) => string | null;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const res = await requestMeta<T>(method, path, options);
  return res.data;
}

/**
 * Same as `request` plus status, `Location` (202 analysis create) and the response's headers (the
 * served window's `Window-From` / `Window-To` on a bare-array read, TMMAPI-0188 U3).
 */
export async function requestMeta<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<RequestMeta<T>> {
  const res = await instance.request<T>(buildConfig(method, path, options));
  const read = (name: string): string | null => {
    const value: unknown = res.headers[name.toLowerCase()];
    return typeof value === 'string' && value.length > 0 ? value : null;
  };
  const location = read('location');
  if (res.status === 204) {
    return { data: undefined as T, status: res.status, location, header: read };
  }
  return {
    data: (res.data === '' ? null : res.data) as T,
    status: res.status,
    location,
    header: read,
  };
}

export interface DownloadResult {
  blob: Blob;
  filename: string | null;
}

/** GET a binary body; the filename comes from `Content-Disposition` when present. */
export async function download(
  path: string,
  options: Omit<RequestOptions, 'json' | 'responseType'> = {},
): Promise<DownloadResult> {
  const res = await instance.request<Blob>(
    buildConfig('GET', path, { ...options, responseType: 'blob' }),
  );
  const header = res.headers['content-disposition'] as string | undefined;
  return { blob: res.data, filename: filenameFromContentDisposition(header) };
}

/** Test seam — the instance, for handler-level assertions. */
export const __instance = instance;
